import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Polygon, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ModuleNav } from "@/components/symin/ModuleNav";
import { SatelliteStreamChip } from "@/components/symin/SatelliteStreamChip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapTrifold, Stack, Crosshair, Polygon as PolyIcon, MapPin, ArrowSquareOut } from "@phosphor-icons/react";

const BASEMAPS = [
  { id: "satellite", label: "Satellite", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attr: "Esri · Maxar", labels: true },
  { id: "dark", label: "Dark Canvas", url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}", attr: "Esri", labels: false },
  { id: "topo", label: "Topographic", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}", attr: "Esri", labels: false },
  { id: "street", label: "Streets", url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attr: "OpenStreetMap", labels: false },
];
const LABELS_URL = "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}";
const PRIO = { "Priority 1": "#C20511", "Priority 2": "#FBBF24", "Priority 3": "#94A3B8" };
const prioColor = (p) => PRIO[p] || "#94A3B8";
const toLatLng = (poly) => (poly || []).map(([x, y]) => [y, x]);

function FitAll({ fc }) {
  const map = useMap();
  useEffect(() => {
    try {
      const b = L.geoJSON(fc).getBounds();
      if (b.isValid()) map.fitBounds(b, { padding: [40, 40] });
    } catch (e) { /* ignore */ }
  }, [fc, map]);
  useEffect(() => { const t = setTimeout(() => map.invalidateSize({ animate: false }), 80); return () => clearTimeout(t); }, [map]);
  return null;
}

function CoordTracker({ set }) {
  const map = useMapEvents({
    mousemove: (e) => set((c) => ({ ...c, lat: e.latlng.lat, lng: e.latlng.lng })),
    zoomend: () => set((c) => ({ ...c, zoom: map.getZoom() })),
  });
  return null;
}

const fmtLat = (v) => `${Math.abs(v).toFixed(4)}° ${v >= 0 ? "N" : "S"}`;
const fmtLng = (v) => `${Math.abs(v).toFixed(4)}° ${v >= 0 ? "E" : "W"}`;

function Toggle({ on, onClick, color, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs hover:bg-accent/50 transition-colors text-left">
      <span className="h-3.5 w-3.5 rounded-[3px] border flex items-center justify-center shrink-0"
        style={{ borderColor: color, background: on ? color : "transparent" }}>
        {on && <span className="h-1.5 w-1.5 rounded-[1px] bg-[#04070B]" />}
      </span>
      <span className={on ? "text-foreground" : "text-muted-foreground"}>{children}</span>
    </button>
  );
}

export default function GisMapSuite() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [basemap, setBasemap] = useState("satellite");
  const [layers, setLayers] = useState({ boundaries: true, targets: true, zones: true });
  const [prios, setPrios] = useState(new Set(["Priority 1", "Priority 2", "Priority 3"]));
  const [areasSel, setAreasSel] = useState(null); // null => all
  const [commodity, setCommodity] = useState("all");
  const [coord, setCoord] = useState({ lat: 0, lng: 0, zoom: 5 });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const r = await api.get(`/projects/${id}/map-data`); setData(r.data); setAreasSel(new Set((r.data.areas || []).map((a) => a.area_id))); }
      catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const bm = BASEMAPS.find((b) => b.id === basemap) || BASEMAPS[0];
  const boundaryFC = useMemo(() => ({
    type: "FeatureCollection",
    features: (data?.areas || []).filter((a) => a.boundary).map((a) => ({
      type: "Feature", properties: { area: a.area_id, name: a.name }, geometry: a.boundary,
    })),
  }), [data]);

  const shown = useMemo(() => (data?.targets || []).filter((t) =>
    (prios.size === 0 || prios.has(t.priority)) &&
    (!areasSel || areasSel.has(t.area)) &&
    (commodity === "all" || t.commodity === commodity)
  ), [data, prios, areasSel, commodity]);

  const togglePrio = (p) => setPrios((s) => { const n = new Set(s); n.has(p) ? n.delete(p) : n.add(p); return n; });
  const toggleArea = (a) => setAreasSel((s) => { const n = new Set(s || []); n.has(a) ? n.delete(a) : n.add(a); return n; });

  const p1 = shown.filter((t) => t.priority === "Priority 1").length;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ModuleNav pid={id} active="map" />
      <div className="ml-[64px] flex flex-col h-screen">
        <header className="h-[52px] shrink-0 bg-card/90 backdrop-blur-md border-b border-border flex items-center gap-2 sm:gap-3 px-3 sm:px-5">
          <MapTrifold size={18} style={{ color: "#38BDF8" }} className="shrink-0" />
          <h1 className="font-display font-semibold text-sm sm:text-lg tracking-tight truncate min-w-0" data-testid="gis-title">GIS Map Suite</h1>
          <span className="text-xs text-muted-foreground truncate hidden lg:block">{data?.project?.name}</span>
          <SatelliteStreamChip testid="gis-stream-chip" />
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <Badge variant="outline" className="text-[10px] whitespace-nowrap" data-testid="gis-target-count">{shown.length} targets</Badge>
            <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => nav(`/projects/${id}/command`)} data-testid="gis-back-command">
              <ArrowSquareOut size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">Command Centre</span>
            </Button>
          </div>
        </header>

        <div className="flex-1 flex min-h-0 flex-col lg:flex-row">
          {/* Layer / filter drawer */}
          <aside className="w-full lg:w-72 shrink-0 border-b lg:border-b-0 lg:border-r border-border bg-card overflow-y-auto max-h-[38vh] lg:max-h-none" data-testid="gis-layer-panel">
            <Section icon={<Stack size={14} />} title="Basemap">
              <div className="grid grid-cols-2 gap-1.5">
                {BASEMAPS.map((b) => (
                  <button key={b.id} data-testid={`basemap-${b.id}`} onClick={() => setBasemap(b.id)}
                    className={`px-2 py-1.5 rounded-md text-xs border transition-colors ${basemap === b.id ? "border-[#38BDF8] text-foreground bg-[#38BDF8]/10" : "border-border text-muted-foreground hover:bg-accent/50"}`}>
                    {b.label}
                  </button>
                ))}
              </div>
            </Section>

            <Section icon={<PolyIcon size={14} />} title="Layers">
              <Toggle testid="layer-boundaries" on={layers.boundaries} color="#C20511" onClick={() => setLayers((l) => ({ ...l, boundaries: !l.boundaries }))}>Licence Boundaries</Toggle>
              <Toggle testid="layer-zones" on={layers.zones} color="#E67332" onClick={() => setLayers((l) => ({ ...l, zones: !l.zones }))}>Prospectivity Zones</Toggle>
              <Toggle testid="layer-targets" on={layers.targets} color="#38BDF8" onClick={() => setLayers((l) => ({ ...l, targets: !l.targets }))}>Exploration Targets</Toggle>
            </Section>

            <Section icon={<Crosshair size={14} />} title="Priority">
              {["Priority 1", "Priority 2", "Priority 3"].map((p) => (
                <Toggle key={p} testid={`filter-${p.replace(" ", "-").toLowerCase()}`} on={prios.has(p)} color={prioColor(p)} onClick={() => togglePrio(p)}>
                  {p} <span className="ml-auto font-mono text-[10px] text-muted-foreground">{(data?.targets || []).filter((t) => t.priority === p).length}</span>
                </Toggle>
              ))}
            </Section>

            {(data?.commodities || []).length > 1 && (
              <Section title="Commodity">
                <div className="flex flex-wrap gap-1.5">
                  {["all", ...(data?.commodities || [])].map((c) => (
                    <button key={c} data-testid={`commodity-${c}`} onClick={() => setCommodity(c)}
                      className={`px-2 py-1 rounded-full text-[11px] border transition-colors ${commodity === c ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent/50"}`}>
                      {c === "all" ? "All" : c}
                    </button>
                  ))}
                </div>
              </Section>
            )}

            <Section title="Licence Areas">
              <div className="space-y-0.5">
                {(data?.areas || []).map((a) => (
                  <Toggle key={a.area_id} testid={`area-${a.area_id}`} on={!areasSel || areasSel.has(a.area_id)} color="#94A3B8" onClick={() => toggleArea(a.area_id)}>
                    <span className="truncate">{a.name}</span>
                    <span className="ml-auto font-mono text-[10px] text-muted-foreground">{a.target_count}</span>
                  </Toggle>
                ))}
              </div>
            </Section>

            <div className="px-3 py-3 mt-1 border-t border-border text-[11px] text-muted-foreground" data-testid="gis-stats">
              Showing <span className="font-mono text-foreground">{shown.length}</span> of {(data?.targets || []).length} targets · <span className="font-mono" style={{ color: "#C20511" }}>{p1}</span> Priority-1
            </div>
          </aside>

          {/* Map canvas */}
          <div className="relative flex-1 min-w-0 bg-[#04070B]">
            {loading ? (
              <div className="absolute inset-0 grid place-items-center text-muted-foreground text-sm">Loading project map…</div>
            ) : (
              <>
                <div className="absolute inset-0 z-0" data-testid="gis-map">
                <MapContainer style={{ height: "100%", width: "100%", background: "#04070B" }} center={[0, 20]} zoom={5} zoomControl={true} attributionControl={true}>
                  <TileLayer key={bm.id} url={bm.url} attribution={`© ${bm.attr}`} maxZoom={19} />
                  {bm.labels && <TileLayer key={`${bm.id}-lbl`} url={LABELS_URL} maxZoom={19} />}
                  {layers.boundaries && boundaryFC.features.length > 0 && (
                    <GeoJSON key={`b-${basemap}`} data={boundaryFC} style={{ color: "#C20511", weight: 2.5, fillColor: "#C20511", fillOpacity: 0.06 }} />
                  )}
                  {layers.zones && shown.filter((t) => (t.polygon || []).length >= 3).map((t, i) => (
                    <Polygon key={`z-${t.id}-${i}`} positions={toLatLng(t.polygon)}
                      pathOptions={{ color: prioColor(t.priority), weight: 1, fillColor: prioColor(t.priority), fillOpacity: 0.25 }} />
                  ))}
                  {layers.targets && shown.map((t, i) => (
                    <CircleMarker key={`t-${t.id}-${i}`} center={[t.lat, t.lon]} radius={5 + (t.prospectivity / 100) * 7}
                      pathOptions={{ color: "#04070B", weight: 1.5, fillColor: prioColor(t.priority), fillOpacity: 0.95 }}>
                      <Popup>
                        <div className="min-w-[190px]" data-testid={`gis-target-popup-${t.id}`}>
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="font-mono font-semibold text-sm text-[#0F172A]">{t.label}</span>
                            <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded-[3px]" style={{ color: "#0F172A", background: prioColor(t.priority) }}>{t.priority.replace("Priority ", "P")}</span>
                          </div>
                          <div className="text-[11px] text-[#475569] mb-2">{t.commodity}{t.deposit_model ? ` · ${t.deposit_model}` : ""} · {t.area_name}</div>
                          {[["Prospectivity", t.prospectivity, "#C20511"], ["Structural", t.structural, "#38BDF8"], ["Spectral", t.spectral, "#A855F7"], ["Confidence", t.confidence, "#10B981"]].map(([lab, val, col]) => (
                            <div key={lab} className="mb-1">
                              <div className="flex justify-between text-[10px] text-[#475569]"><span>{lab}</span><span className="font-mono" style={{ color: col }}>{val}%</span></div>
                              <div className="h-1 rounded-full bg-[#E2E8F0] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${val}%`, background: col }} /></div>
                            </div>
                          ))}
                        </div>
                      </Popup>
                    </CircleMarker>
                  ))}
                  <FitAll fc={boundaryFC} />
                  <CoordTracker set={setCoord} />
                </MapContainer>
                </div>

                {/* Coordinate readout */}
                <div className="absolute bottom-3 left-3 z-[500] flex items-center gap-2 px-3 py-1.5 rounded-md bg-card/90 backdrop-blur-md border border-border font-mono text-[11px] text-foreground" data-testid="gis-coord-readout">
                  <MapPin size={12} style={{ color: "#38BDF8" }} />
                  {fmtLat(coord.lat)} &nbsp;·&nbsp; {fmtLng(coord.lng)} &nbsp;·&nbsp; <span className="text-muted-foreground">WGS84 · z{coord.zoom}</span>
                </div>

                {/* Legend */}
                <div className="absolute top-3 right-3 z-[500] px-3 py-2.5 rounded-md bg-card/90 backdrop-blur-md border border-border" data-testid="gis-legend">
                  <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-1.5">Target Priority</div>
                  {["Priority 1", "Priority 2", "Priority 3"].map((p) => (
                    <div key={p} className="flex items-center gap-2 text-[11px] text-foreground mb-0.5">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: prioColor(p) }} />{p}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <div className="px-3 py-3 border-b border-border">
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-2">{icon}{title}</div>
      {children}
    </div>
  );
}
